import * as Phaser from "phaser";
import {
  AssetLocator,
  type AccessoryType as AssetAccessoryType,
  type Animation,
  DEFAULT_ANIMATION,
} from "./AssetLocator";
import * as CatAnims from "./CatAnimations";
import { HeartSprite } from "./HeartSprite";
import { AccessorySprite } from "./AccessorySprite";
import { EffectSprite } from "./EffectSprite";
import { SHOW_DEBUG } from "../../config";

// Regex to extract numeric frame suffix (e.g. modularchar_idle_07.png -> 7)
const FRAME_INDEX_RE = /_(\d+)\.png$/;

interface CatOptions {
  catNumber?: number; // Cat number (1-15), defaults to 5
  clothes?: string[]; // Changed from specific type to allow for future clothes
  hat?: string | null;
  glasses?: string | null;
  layerOverrides?: { [part: string]: number };
}

// Use AssetLocator types for consistency
type AccessoryType = AssetAccessoryType;

export class Cat extends Phaser.GameObjects.Container {
  private static animationsCreated = new Set<string>();

  // Default render order (low to high depth)
  private static readonly DEFAULT_DEPTHS: { [key: string]: number } = {
    leg_b: 0,
    hand_b: 1,
    tail: 2,
    clothes_b: 3,
    hat: 4,
    body: 5,
    clothes_f: 6,
    leg_f: 7,
    hat_f: 8,
    glasses: 9,
    hand_f: 10,
    heart: 11, // Add heart as highest layer
  };

  private sprites: { [key: string]: Phaser.GameObjects.Sprite } = {};
  private equipped: { [K in AccessoryType]?: string } = {};
  private currentAnimation: string | null = null;
  private layerOverrides: { [part: string]: number };
  private _isFacingLeft: boolean = false;
  private heartSprite?: HeartSprite;
  private assetLocator: AssetLocator;
  private directionChangeTimer?: Phaser.Time.TimerEvent;
  /** Timer for any pending transition back to idle; cleared when animation changes */
  private idleTransitionTimer?: Phaser.Time.TimerEvent;
  private accessories: { [K in AccessoryType]?: AccessorySprite } = {};
  private effectSprite?: EffectSprite;
  /**
   * Internal flag indicating the cat has just exited a stunned state and is
   * currently playing the one-off recovery jump.  Used to avoid an immediate
   * idle override from a second refreshFinancialStatus call in the same tick.
   */
  private recoveringFromStunned: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    options: CatOptions = {},
  ) {
    super(scene, x, y);

    const catNumber = options.catNumber || 5;
    this.assetLocator = new AssetLocator(catNumber);
    this.layerOverrides = options.layerOverrides || {};

    // Create all base cat body part sprites
    this.createBaseSprites(scene, this.layerOverrides);

    // Equip initial items
    if (options.clothes) {
      options.clothes.forEach((clothesKey) =>
        this.equip("clothes", clothesKey),
      );
    }
    if (options.hat) {
      this.equip("hat", options.hat);
    }
    if (options.glasses) {
      this.equip("glasses", options.glasses);
    }

    // Make the cat container interactive for click detection
    // Adjusted interactive area to account for visual centering offset
    this.setInteractive(
      new Phaser.Geom.Rectangle(-100, -112.5, 200, 225), // Adjusted click area for centered cat
      Phaser.Geom.Rectangle.Contains,
    );

    // Start with default animation
    this.animate(DEFAULT_ANIMATION, true);

    // Add debug rectangle around cat sprite
    if (SHOW_DEBUG) {
      const debugRect = scene.add.rectangle(0, 0, 200, 225, 0x00ff00, 0.3);
      debugRect.setStrokeStyle(3, 0x00ff00);
      debugRect.setDepth(1000); // Very high depth to be on top
      this.add(debugRect);
    }
  }

  static loadAssetsForCat = CatAnims.loadAssetsForCat;
  private static createAnimationsForCat = CatAnims.createAnimationsForCat;

  private createBaseSprites(
    scene: Phaser.Scene,
    layerOverrides: { [part: string]: number },
  ): void {
    // Create base body parts
    const currentAnim = DEFAULT_ANIMATION;
    const bodyAssets = this.assetLocator.getAllBodyAssets(currentAnim);

    // Calculate visual centering offset
    // Based on interactive area: Rectangle(-100, -250, 200, 225)
    // Visual center is at -250 + 225/2 = -137.5
    // To center the visual content, we need to offset sprites down by 137.5
    const VISUAL_CENTER_OFFSET = 137.5;

    Object.entries(bodyAssets).forEach(([partName, asset]) => {
      const sprite = scene.add.sprite(0, VISUAL_CENTER_OFFSET, asset.key);
      // Fix anchor point for sprites with embedded effects (hammer, umbrella, stunned stars, etc.)
      // The JSON files have anchor points around y: 0.88, causing effects to appear too high
      sprite.setOrigin(0.5, 0.5); // Center the sprite instead of using the JSON anchor point
      const depth =
        layerOverrides[partName] ?? Cat.DEFAULT_DEPTHS[partName] ?? 0;
      sprite.setDepth(depth);
      this.sprites[partName] = sprite;
      this.add(sprite);
    });

    // Sort children by depth
    this.sortChildrenByDepth();
  }

  private sortChildrenByDepth(): void {
    this.list.sort((a, b) => {
      const aDepth = (a as Phaser.GameObjects.Sprite).depth;
      const bDepth = (b as Phaser.GameObjects.Sprite).depth;
      return aDepth - bDepth;
    });
  }

  equip(type: AccessoryType, key: string): void {
    // Early-out if already wearing that exact accessory
    if (this.accessories[type]?.getKey() === key) return;

    // Remove previous one (if any)
    this.unequip(type);

    // Remember simple string mapping so other legacy code (getAnimationKey) still works
    this.equipped[type] = key;

    // Create wrapper and register
    const depthMap = { ...Cat.DEFAULT_DEPTHS, ...this.layerOverrides };
    const accessory = new AccessorySprite(
      this,
      this.scene,
      this.assetLocator,
      type,
      key,
      this._isFacingLeft,
      depthMap,
    );

    this.accessories[type] = accessory;

    // Immediately sync to current animation and align frames to body progress (if available)
    const bodySprite = this.sprites.body;
    const progress =
      bodySprite && bodySprite.anims ? bodySprite.anims.getProgress() : 0;
    accessory.play(
      (this.currentAnimation as Animation) || DEFAULT_ANIMATION,
      true,
      progress,
    );

    // Ensure display list ordering obeys depths set on individual sprites
    this.sortChildrenByDepth();
  }

  unequip(type: AccessoryType): void {
    const accessory = this.accessories[type];
    if (!accessory) return;

    accessory.destroy(this);
    delete this.accessories[type];
    delete this.equipped[type];
  }

  animate(name: Animation, loop: boolean = true): void {
    // Safety check: ensure scene exists before proceeding
    if (!this.scene) {
      console.warn("Cat.animate called but scene is undefined");
      return;
    }

    this.currentAnimation = name;

    // Clean up existing direction change timer
    if (this.directionChangeTimer) {
      this.directionChangeTimer.destroy();
      this.directionChangeTimer = undefined;
    }

    // Clear any queued idle transition to avoid race conditions (e.g. stunned recovery)
    if (this.idleTransitionTimer) {
      this.idleTransitionTimer.destroy();
      this.idleTransitionTimer = undefined;
    }

    // Hide current effect if any (will be replaced below if necessary)
    this.hideEffect();

    // Only randomly change direction when idling
    if (loop && name === "idle") {
      this.directionChangeTimer = this.scene.time.addEvent({
        delay: Phaser.Math.Between(2000, 4000),
        callback: () => {
          this.setFacingLeft(Math.random() > 0.5);
        },
        loop: true,
      });
    }

    // Play animation on all base body part sprites
    Object.entries(this.sprites).forEach(([partName, sprite]) => {
      const animKey = this.getAnimationKey(partName, name);
      if (
        animKey &&
        this.scene &&
        this.scene.anims &&
        this.scene.anims.exists(animKey)
      ) {
        sprite.play({ key: animKey, repeat: loop ? -1 : 0 });
      }
    });

    // Sync accessories and align to current progress
    const bodySprite = this.sprites.body;
    const progress =
      bodySprite && bodySprite.anims && this.scene
        ? bodySprite.anims.getProgress()
        : 0;
    Object.values(this.accessories).forEach((acc) =>
      acc.play(name, loop, progress),
    );

    // Add effect if animation maps to one
    if (AssetLocator.hasEffect(name)) {
      this.showEffect(name);
    }
  }

  /**
   * Set cat to idle animation (continuously running)
   */
  idle(): void {
    this.animate(DEFAULT_ANIMATION, true);
  }

  private getAnimationKey(
    partName: string,
    animationName: string,
  ): string | null {
    // Cast animationName to Animation type for AssetLocator
    const animation = animationName as Animation;

    // For body parts, use the AssetLocator
    if (
      ["body", "hand_f", "hand_b", "leg_b", "leg_f", "tail"].includes(partName)
    ) {
      const asset = this.assetLocator.getBodyPartAsset(
        partName as keyof import("./AssetLocator").CatAssetMap,
        animation,
      );
      return asset.key;
    }

    // For equipped accessories, construct the key using AssetLocator
    if (partName.includes("clothes")) {
      const type = partName.includes("_b") ? "b" : "f";
      const clothesKey = this.equipped.clothes;
      if (clothesKey) {
        const asset = this.assetLocator.getAccessoryAsset(
          "clothes",
          clothesKey,
          animation,
          type as "b" | "f",
        );
        return asset.key;
      }
    } else if (partName === "hat" && this.equipped.hat) {
      const asset = this.assetLocator.getAccessoryAsset(
        "hat",
        this.equipped.hat,
        animation,
      );
      return asset.key;
    } else if (partName === "glasses" && this.equipped.glasses) {
      const asset = this.assetLocator.getAccessoryAsset(
        "glasses",
        this.equipped.glasses,
        animation,
      );
      return asset.key;
    }

    console.warn(`Animation key not found for: ${partName}_${animationName}`);
    return null;
  }

  // Add getter for direction
  public get isFacingLeft(): boolean {
    return this._isFacingLeft;
  }

  public getCatNumber(): number {
    return this.assetLocator.getCatNumber();
  }

  // Add method to flip the cat
  public setFacingLeft(isLeft: boolean): void {
    if (this._isFacingLeft === isLeft) return;

    this._isFacingLeft = isLeft;

    // Flip base body sprites
    Object.values(this.sprites).forEach((sprite) => sprite.setFlipX(isLeft));

    // Flip accessories
    Object.values(this.accessories).forEach((acc) => acc.setFlipX(isLeft));

    // Recreate effect so its internal positioning matches direction
    if (this.effectSprite && this.currentAnimation) {
      this.effectSprite.destroy();
      const depthMap = { ...Cat.DEFAULT_DEPTHS, ...this.layerOverrides };
      this.effectSprite = new EffectSprite(
        this,
        this.scene,
        this.currentAnimation as Animation,
        this._isFacingLeft,
        depthMap,
      );
      this.sortChildrenByDepth();
    }

    this.updateHeartPosition();
    this.emit("directionChange", isLeft);
  }

  public showHeart(): void {
    if (!this.scene) return; // Safety check
    // Check if heart sprite should be hidden based on hint visibility
    const hideHeartSprite = this.scene.registry.get("hideHeartSprite");
    if (hideHeartSprite) {
      return; // Don't show heart if it should be hidden
    }

    if (!this.heartSprite) {
      // Adjust heart position to account for visual centering offset
      this.heartSprite = new HeartSprite(this.scene, 0, -150 + 137.5);
      this.heartSprite.setDepth(Cat.DEFAULT_DEPTHS.heart);
      this.add(this.heartSprite);
      this.updateHeartPosition();
    }
  }

  public hideHeart(): void {
    if (!this.scene) return;
    if (this.heartSprite) {
      this.heartSprite.destroy();
      this.heartSprite = undefined;
    }
  }

  /**
   * Force refresh heart visibility based on current hint settings
   * This should be called when hint visibility changes
   */
  public refreshHeartVisibility(): void {
    const hideHeartSprite = this.scene.registry.get("hideHeartSprite");
    if (hideHeartSprite && this.heartSprite) {
      // If hearts should be hidden and we have one, hide it
      this.hideHeart();
    }
  }

  /**
   * Update the heart visibility based on budget / spent values. This becomes
   * the single authoritative place for deciding whether the cat should show a
   * heart (surplus) or not.
   */
  public refreshFinancialStatus(budgeted: number, spent: number): void {
    // If cat is currently walking towards its destination, avoid switching animations mid-move
    if (this.currentAnimation === "walk") {
      // Still allow heart visibility update after the move completes via idle refresh
      return;
    }

    // ----- 1. Overspent – show stunned state -----
    if (spent > budgeted) {
      // Deficit: want to stay / switch to stunned
      this.hideHeart();

      if (this.currentAnimation !== "stunned") {
        this.animate("stunned", true);
      }
      return;
    }

    // Guard: if we are still in the recovery jump, ignore duplicate refreshes
    // that could otherwise cancel the jump mid-air. Simply bail out – the cat
    // will process the heart/idle transition at the end of its jump.
    if (this.recoveringFromStunned) {
      return;
    }

    // Determine if the cat should display a heart once it reaches idle
    const shouldShowHeart = budgeted > spent;

    // ----- 2. Previously stunned → now balanced / surplus -----
    // If the cat was in a stunned loop, let it perform a single jump before settling to idle.
    if (this.currentAnimation === "stunned") {
      // Play jump once (non-looping)
      this.animate("jump", false);

      // Mark as recovering so additional refreshes during the same JS tick
      // (e.g. duplicate calls from TransferSystem and BudgetSceneReact) do not
      // prematurely switch the cat to idle.
      this.recoveringFromStunned = true;

      // After the jump animation duration, transition to idle and update heart
      // We reuse the same timings used by jumpInJoy (≈800 ms) for consistency.
      this.idleTransitionTimer = this.scene.time.delayedCall(800, () => {
        if (!this.scene) return; // safety if cat destroyed in between

        // Recovery finished – clear flag and settle to idle
        this.recoveringFromStunned = false;
        this.animate("idle", true);

        if (shouldShowHeart) {
          this.showHeart();
        } else {
          this.hideHeart();
        }
      });

      return; // early-exit – rest handled in delayed callback
    }

    // ----- 3. Normal balanced / surplus transition (not coming from stunned) -----

    const heartCurrentlyVisible = !!this.heartSprite;

    // If already idling with correct heart visibility, no-op to avoid restarting animation
    if (
      this.currentAnimation === "idle" &&
      heartCurrentlyVisible === shouldShowHeart
    ) {
      return;
    }

    if (this.currentAnimation !== "idle") {
      this.animate("idle", true);
    }

    // Update heart visibility only if it changed
    if (shouldShowHeart && !heartCurrentlyVisible) {
      this.showHeart();
    } else if (!shouldShowHeart && heartCurrentlyVisible) {
      this.hideHeart();
    }
  }

  public jumpInJoy(): void {
    if (!this.scene) return; // Safety check
    // Trigger the jump animation but run it once (not looped)
    this.animate("jump", false); // Play jump animation once, not looped

    // Add some excitement by briefly showing heart if not already shown
    const wasHeartVisible = !!this.heartSprite;
    if (!wasHeartVisible) {
      this.showHeart();
      // Hide heart after joy animation and return to idle
      this.idleTransitionTimer = this.scene.time.delayedCall(1200, () => {
        if (this.scene) {
          // Safety check
          this.hideHeart();
          this.idle(); // Return to idle pose
        }
      });
    } else {
      // If heart was already visible, just return to idle after animation
      this.idleTransitionTimer = this.scene.time.delayedCall(800, () => {
        if (this.scene) {
          // Safety check
          this.idle(); // Return to idle pose
        }
      });
    }
  }

  /**
   * Make cat start jumping continuously (for special occasions)
   */
  startJumping(): void {
    this.animate("jump", true);
  }

  private updateHeartPosition(): void {
    if (this.heartSprite) {
      this.heartSprite.x = this._isFacingLeft ? -100 : 100;
    }
  }

  private showEffect(animation: Animation): void {
    // Always start with a clean slate
    this.hideEffect();

    const depthMap = { ...Cat.DEFAULT_DEPTHS, ...this.layerOverrides };
    this.effectSprite = new EffectSprite(
      this,
      this.scene,
      animation,
      this._isFacingLeft,
      depthMap,
    );
    this.sortChildrenByDepth();
  }

  private hideEffect(): void {
    if (this.effectSprite) {
      this.effectSprite.destroy();
      this.effectSprite = undefined;
    }
  }

  destroy() {
    Object.values(this.sprites).forEach((sprite) => sprite.destroy());
    this.heartSprite?.destroy();

    // Clean up effect sprite
    if (this.effectSprite) {
      this.effectSprite.destroy();
      this.effectSprite = undefined;
    }

    // Clean up direction change timer
    if (this.directionChangeTimer) {
      this.directionChangeTimer.destroy();
      this.directionChangeTimer = undefined;
    }

    if (this.idleTransitionTimer) {
      this.idleTransitionTimer.destroy();
      this.idleTransitionTimer = undefined;
    }

    // Clean up accessories
    Object.values(this.accessories).forEach((acc) => acc.destroy(this));
    this.accessories = {} as any;

    super.destroy();
  }

  /** List of animations required for initial gameplay load */
  public static readonly PRIORITY_ANIMATIONS: Animation[] =
    CatAnims.PRIORITY_ANIMATIONS;

  /**
   * Queue loading of ALL cat body & accessory atlases (cats 1-15, every animation).
   * Should be called during a Scene.preload().
   */
  public static queueAllAssets(
    scene: Phaser.Scene,
    animations: Animation[] = Cat.PRIORITY_ANIMATIONS,
  ): void {
    const allAnimations = animations;

    // Body parts per cat
    for (let catNumber = 1; catNumber <= 15; catNumber++) {
      const locator = new AssetLocator(catNumber);
      allAnimations.forEach((anim) => {
        const bodyAssets = locator.getAllBodyAssets(anim);
        Object.values(bodyAssets).forEach((asset) => {
          scene.load.atlas(
            asset.key,
            `assets/${asset.imagePath}`,
            `assets/${asset.jsonPath}`,
          );
        });
      });
    }

    // Accessories – they are shared, so catNumber 1 suffices for paths
    const accessoryLocator = new AssetLocator(1);
    allAnimations.forEach((anim) => {
      // Clothes
      AssetLocator.getAvailableAccessories("clothes").forEach((clothesName) => {
        const clothesAssets = accessoryLocator.getClothesAssets(
          clothesName,
          anim,
        );
        scene.load.atlas(
          clothesAssets.back.key,
          `assets/${clothesAssets.back.imagePath}`,
          `assets/${clothesAssets.back.jsonPath}`,
        );
        if (clothesAssets.front.key !== clothesAssets.back.key) {
          scene.load.atlas(
            clothesAssets.front.key,
            `assets/${clothesAssets.front.imagePath}`,
            `assets/${clothesAssets.front.jsonPath}`,
          );
        }
      });

      // Hats
      AssetLocator.getAvailableAccessories("hat").forEach((hatName) => {
        const hatAsset = accessoryLocator.getAccessoryAsset(
          "hat",
          hatName,
          anim,
        );
        scene.load.atlas(
          hatAsset.key,
          `assets/${hatAsset.imagePath}`,
          `assets/${hatAsset.jsonPath}`,
        );
      });

      // Glasses
      AssetLocator.getAvailableAccessories("glasses").forEach((glassesName) => {
        const glassesAsset = accessoryLocator.getAccessoryAsset(
          "glasses",
          glassesName,
          anim,
        );
        scene.load.atlas(
          glassesAsset.key,
          `assets/${glassesAsset.imagePath}`,
          `assets/${glassesAsset.jsonPath}`,
        );
      });
    });
  }

  /**
   * Create animations for every loaded cat body & accessory asset.
   * Call during Scene.create() *after* the atlas textures are in cache.
   */
  public static createAllAnimations(
    scene: Phaser.Scene,
    animations: Animation[] = Cat.PRIORITY_ANIMATIONS,
  ): void {
    const allAnimations = animations;
    const allAssetKeys: string[] = [];

    // Gather body asset keys
    for (let catNumber = 1; catNumber <= 15; catNumber++) {
      const locator = new AssetLocator(catNumber);
      allAnimations.forEach((anim) => {
        const bodyAssets = locator.getAllBodyAssets(anim);
        allAssetKeys.push(
          ...Object.values(bodyAssets).map((asset) => asset.key),
        );
      });
    }

    // Accessories (use locator 1)
    const accessoryLocator = new AssetLocator(1);
    allAnimations.forEach((anim) => {
      AssetLocator.getAvailableAccessories("clothes").forEach((clothesName) => {
        const clothesAssets = accessoryLocator.getClothesAssets(
          clothesName,
          anim,
        );
        allAssetKeys.push(clothesAssets.back.key);
        if (clothesAssets.front.key !== clothesAssets.back.key) {
          allAssetKeys.push(clothesAssets.front.key);
        }
      });

      AssetLocator.getAvailableAccessories("hat").forEach((hatName) => {
        const hatAsset = accessoryLocator.getAccessoryAsset(
          "hat",
          hatName,
          anim,
        );
        allAssetKeys.push(hatAsset.key);
      });

      AssetLocator.getAvailableAccessories("glasses").forEach((glassesName) => {
        const glassesAsset = accessoryLocator.getAccessoryAsset(
          "glasses",
          glassesName,
          anim,
        );
        allAssetKeys.push(glassesAsset.key);
      });
    });

    // Create animations where missing
    allAssetKeys.forEach((assetKey) => {
      if (!scene.textures.exists(assetKey) || scene.anims.exists(assetKey))
        return;

      const frames = scene.anims.generateFrameNames(assetKey).sort((a, b) => {
        const getIdx = (f: Phaser.Types.Animations.AnimationFrame) =>
          Number(String(f.frame).match(FRAME_INDEX_RE)?.[1] ?? "0");
        return getIdx(a) - getIdx(b);
      });

      scene.anims.create({
        key: assetKey,
        frames,
        frameRate: 24,
        repeat: -1,
      });

      Cat.animationsCreated.add(assetKey);
    });
  }
}

/*
Usage Example:

await Cat.loadAssetsForCat(this, 5, 'idle');
const cat = new Cat(this, 400, 300, {
  catNumber: 5,
  clothes: ['yellow_shirt'],
  hat: 'crown'
});
cat.animate('idle', true);
this.add.existing(cat);
*/
